import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ProductCodePipe } from 'src/app/core/pipes/product-code.pipe';

export interface ISegPreviewLine {
  id: number;
  product_name: string;
  sku: string | null;
  /** Optional until the segregation-preview payload carries it; the resolver
   *  falls back to `sku` in the meantime (WB-7419 / WB-7451). */
  erp_id?: string | null;
  qty: number;
  price_unit: number;
  total: number;
}

export interface ISegPreviewGroup {
  split_key: string;
  split_label: string;
  condition_type: string;
  line_count: number;
  lines: ISegPreviewLine[];
}

export interface ISegPreviewMechanism {
  id: number;
  name: string;
  condition_type: string;
  custom_field_name: string | null;
}

export interface ISegregationPreviewModalData {
  orderId: number;
  orderName: string;
  preview: {
    mechanism: ISegPreviewMechanism;
    groups: ISegPreviewGroup[];
  };
}

export type SegregationPreviewResult =
  | null
  | { action: 'skip' }
  | { action: 'confirm'; mechanism_id: number };

const LINES_INITIAL = 5;

@Component({
  selector: 'app-segregation-preview-modal',
  standalone: true,
  imports: [CommonModule, MatDialogModule, TranslateModule, ProductCodePipe],
  templateUrl: './segregation-preview-modal.component.html',
  styleUrls: ['./segregation-preview-modal.component.scss'],
})
export class SegregationPreviewModalComponent {
  readonly LINES_INITIAL = LINES_INITIAL;
  expandedGroups = new Set<number>();

  constructor(
    private dialogRef: MatDialogRef<SegregationPreviewModalComponent, SegregationPreviewResult>,
    @Inject(MAT_DIALOG_DATA) public data: ISegregationPreviewModalData,
    private translate: TranslateService,
  ) {}

  get groups(): ISegPreviewGroup[] { return this.data.preview.groups; }
  get mechanism(): ISegPreviewMechanism { return this.data.preview.mechanism; }

  conditionLabel(type: string): string {
    if (type === 'product_custom_field' && this.mechanism.custom_field_name) {
      return this.mechanism.custom_field_name;
    }
    const key = `segregation_modal.condition_types.${type}`;
    const translated = this.translate.instant(key);
    return translated === key
      ? this.translate.instant('segregation_modal.condition_types.default')
      : translated;
  }

  trackByGroupKey(_index: number, group: ISegPreviewGroup): string { return group.split_key; }

  toggleExpand(i: number): void {
    if (this.expandedGroups.has(i)) this.expandedGroups.delete(i);
    else this.expandedGroups.add(i);
  }

  visibleLines(group: ISegPreviewGroup, i: number): ISegPreviewLine[] {
    return this.expandedGroups.has(i) ? group.lines : group.lines.slice(0, LINES_INITIAL);
  }

  hiddenCount(group: ISegPreviewGroup, i: number): number {
    return this.expandedGroups.has(i) ? 0 : Math.max(0, group.lines.length - LINES_INITIAL);
  }

  confirm(): void {
    this.dialogRef.close({ action: 'confirm', mechanism_id: this.mechanism.id });
  }

  skipSplit(): void {
    this.dialogRef.close({ action: 'skip' });
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
